/**
 * WhatsApp, phase 1 (owner, 2026-10-24; MKK module 37): the mosque's groups, channels and contacts remembered, a
 * message composed from a notice, an event, a banner or free text, and shared from the staff member's OWN phone.
 *
 * Why nothing is sent from here: the official WhatsApp Business Cloud API cannot post to a group or a channel, and
 * the unofficial bridges that drive WhatsApp Web break WhatsApp's terms. So the platform prepares the text (and the
 * picture, where the source has one), the person hands it to WhatsApp through the phone's share sheet or a wa.me
 * link, picks the group there, and the platform logs what was shared. Phase 2 (the Cloud API, 1:1 to opted-in members
 * with approved templates) is designed in docs/platform-implementation/WHATSAPP.md and not built.
 *
 * Pure: validators return { ok, value } or { ok: false, errors }; the composer takes what was published and the
 * mosque's public name and website, and never a clock. The caption rules (the title in *bold*, the mosque's link, the
 * 2 000-character cut) are social-share.js's — one WhatsApp text, wherever it is made.
 */
const S = require('./social-share');
const { formatDate } = require('./i18n');

/** What a target is: a group's invite link, a channel's link, or one person's wa.me number. */
const KINDS = ['group', 'channel', 'contact'];
const KIND_NO = { group: 'Gruppe', channel: 'Kanal', contact: 'Kontakt' };
/** What a message is made from. */
const SOURCES = ['notice', 'event', 'banner', 'free'];
const SOURCE_NO = { notice: 'Kunngjøring', event: 'Arrangement', banner: 'Banner', free: 'Fritekst' };
/** The phone (or the panel) it was shared from. */
const DEVICES = ['ios', 'android', 'web'];
const MAX = { name: 80, url: 300, note: 200, text: S.NETWORKS.whatsapp.max };
/** At most this many targets per mosque (archived ones included): a list staff read, not an address book. */
const MAX_TARGETS = 100;
/** How many shares the log answers with at most. */
const LOG_LIMIT = 200;

const clean = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
/** An id as the platform makes them (uuids; the seeds' too). Anything else is never looked up. */
const isId = (v) => typeof v === 'string' && ID_RE.test(v);

/**
 * A WhatsApp link, checked by what WhatsApp itself issues:
 *   group    https://chat.whatsapp.com/<invite code>
 *   channel  https://whatsapp.com/channel/<id>   (www. accepted, dropped)
 *   contact  https://wa.me/<E.164 number, digits only, 7–15>   (a leading + is dropped)
 * → { ok, value, kind } or { ok: false, error }. The kind is read from the link, so a group link filed as a channel
 * is refused rather than stored under the wrong name.
 */
function inviteUrl(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return { ok: false, error: 'required' };
  if (s.length > MAX.url) return { ok: false, error: 'too_long' };
  let u;
  try { u = new URL(/^https?:\/\//i.test(s) ? s : 'https://' + s); } catch { return { ok: false, error: 'invalid' }; }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return { ok: false, error: 'invalid' };
  const host = u.hostname.toLowerCase().replace(/^www\./, '');
  const path = u.pathname.replace(/\/+$/, '');
  if (host === 'chat.whatsapp.com' && /^\/[A-Za-z0-9_-]{8,64}$/.test(path)) return { ok: true, value: `https://chat.whatsapp.com${path}`, kind: 'group' };
  if (host === 'whatsapp.com' && /^\/channel\/[A-Za-z0-9_-]{8,64}$/.test(path)) return { ok: true, value: `https://whatsapp.com${path}`, kind: 'channel' };
  if (host === 'wa.me') {
    const digits = path.replace(/^\/\+?/, '');
    if (/^[1-9][0-9]{6,14}$/.test(digits)) return { ok: true, value: `https://wa.me/${digits}`, kind: 'contact' };
  }
  return { ok: false, error: 'invalid' };
}

const FIELDS = ['kind', 'name', 'inviteUrl', 'note', 'sort'];

/**
 * A target as staff write it: { kind, name, inviteUrl, note?, sort? } → { ok, value, changed } or { ok: false, errors }.
 * With `prev` (a change), a field not sent keeps its value, and `changed` names the fields that differ. Unknown fields
 * are refused (a client sending `archived` or `id` here has misunderstood the route).
 */
function targetInput(body, prev = null) {
  const raw = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const errors = {};
  for (const k of Object.keys(raw)) if (!FIELDS.includes(k)) errors[k] = 'Ukjent felt.';
  const b = prev ? { kind: prev.kind, name: prev.name, inviteUrl: prev.inviteUrl, note: prev.note, sort: prev.sort, ...raw } : raw;
  const kind = KINDS.includes(b.kind) ? b.kind : null;
  if (!kind) errors.kind = 'Velg gruppe, kanal eller kontakt.';
  const name = clean(b.name);
  if (!name) errors.name = 'Skriv et navn.';
  else if (name.length > MAX.name) errors.name = `Navnet kan være høyst ${MAX.name} tegn.`;
  const link = inviteUrl(b.inviteUrl);
  if (!link.ok) errors.inviteUrl = link.error === 'required' ? 'Lim inn lenken.' : link.error === 'too_long' ? 'Lenken er for lang.'
    : 'Lenken må være en invitasjon fra WhatsApp (chat.whatsapp.com/…, whatsapp.com/channel/… eller wa.me/<nummer>).';
  else if (kind && link.kind !== kind) errors.inviteUrl = `Dette er en lenke til en ${KIND_NO[link.kind].toLowerCase()}, ikke en ${KIND_NO[kind].toLowerCase()}.`;
  const note = clean(b.note);
  if (note.length > MAX.note) errors.note = `Notatet kan være høyst ${MAX.note} tegn.`;
  let sort = 0;
  if (b.sort !== undefined && b.sort !== null && b.sort !== '') {
    sort = Number(b.sort);
    if (!Number.isInteger(sort) || sort < 0 || sort > 999) errors.sort = 'Rekkefølgen må være et tall fra 0 til 999.';
  }
  if (Object.keys(errors).length) return { ok: false, errors };
  const value = { kind, name, inviteUrl: link.value, note: note || null, sort };
  const changed = prev ? FIELDS.filter((k) => (value[k] ?? null) !== (prev[k] ?? null)) : FIELDS.slice();
  return { ok: true, value, changed };
}

/** Archive a target: it leaves the lists and the pickers; the shares that named it keep its name. */
function archive(target, at) {
  if (!target) return { ok: false, code: 'not_found', message: 'Fant ikke gruppen.' };
  if (target.archivedAt) return { ok: false, code: 'already_archived', message: 'Den er allerede arkivert.' };
  return { ok: true, value: { ...target, archivedAt: at } };
}
/** Bring an archived target back. */
function restore(target) {
  if (!target) return { ok: false, code: 'not_found', message: 'Fant ikke gruppen.' };
  if (!target.archivedAt) return { ok: false, code: 'not_archived', message: 'Den er ikke arkivert.' };
  return { ok: true, value: { ...target, archivedAt: null } };
}

/** Targets as they are listed: the mosque's order, then by name; archived ones last (or left out). */
function sortTargets(list, { withArchived = false } = {}) {
  return (list || []).filter((t) => withArchived || !t.archivedAt)
    .slice().sort((a, b) => Boolean(a.archivedAt) - Boolean(b.archivedAt) || (a.sort || 0) - (b.sort || 0) || String(a.name).localeCompare(String(b.name), 'nb'));
}

/** "2026-10-04T18:00:00" (the mosque's wall clock) → { date: Date (local parts), hm: '18:00', day } or null. */
function wall(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(String(v || ''));
  if (!m) return null;
  return { date: new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), hm: m[4] ? `${m[4]}:${m[5]}` : null, day: m.slice(1, 4).join('-') };
}

/**
 * When an event is, in Norwegian, as the text says it: «lørdag 4. oktober 2026 kl. 18:00–20:00» (the end only on the
 * same day; another day is written out). Wall-clock strings in, nothing about zones.
 */
function whenText(startsAt, endsAt) {
  const a = wall(startsAt);
  if (!a) return '';
  const b = wall(endsAt);
  let out = formatDate(a.date, 'no') + (a.hm ? ` kl. ${a.hm}` : '');
  if (b && b.day === a.day && b.hm && a.hm) out += `–${b.hm}`;
  else if (b && b.day !== a.day) out += ` – ${formatDate(b.date, 'no')}${b.hm ? ` kl. ${b.hm}` : ''}`;
  return out;
}

/**
 * Is this source something that may go to a group? Published, public content only — never a draft, a janazah, a
 * members-only event. A banner: approved (live or scheduled; social-share's rule). A notice: an ordinary one that is
 * up. An event: published (not cancelled) and public.
 */
function shareable(kind, item) {
  if (!item) return false;
  if (kind === 'notice' || kind === 'banner') return S.shareable(kind, item);
  if (kind === 'event') return item.status === 'published' && (item.visibility || 'public') === 'public';
  if (kind === 'free') return true;
  return false;
}

/**
 * The WhatsApp text for one source:
 *   { source: 'notice'|'event'|'banner'|'free', item, mosque: { name, website } } → { text, truncated }
 * A notice: its title and text. An event: title, when, where, text. A banner: its headline, then its other lines (a
 * picture banner: its heading). Free text: as typed. All but free text end with the mosque's name and its website (a
 * clickable link in WhatsApp), the title in *bold*, cut to WhatsApp's 2 000 characters at a word —
 * social-share.caption, the same text «Del» makes.
 */
function compose({ source, item = {}, mosque = {} } = {}) {
  if (source === 'free') {
    const t = String(item.text == null ? '' : item.text).replace(/\r\n?/g, '\n').trim();
    if (t.length <= MAX.text) return { text: t, truncated: false };
    const c = S.caption({ title: '', body: t, mosque: {}, network: 'whatsapp' });
    return { text: c.text, truncated: c.truncated };
  }
  let title = '', body = '';
  if (source === 'notice') { title = item.title || ''; body = item.body || ''; }
  else if (source === 'event') {
    title = item.title || '';
    const when = whenText(item.startsAt, item.endsAt);
    body = [when ? `🗓 ${when}` : '', item.location ? `📍 ${String(item.location).trim()}` : '', item.body || ''].filter(Boolean).join('\n');
  } else if (source === 'banner') {
    const l = item.layers || {};
    title = l.headline || l.eyebrow || item.title || '';
    body = ['detail', 'when', 'where'].map((k) => String(l[k] || '').trim()).filter(Boolean).join('\n');
  } else return { text: '', truncated: false };
  const c = S.caption({ title, body, mosque, network: 'whatsapp' });
  return { text: c.text, truncated: c.truncated };
}

/** One line for a source in a picker: { kind, id, title, when } — what step 1 lists. */
function sourceLine(kind, item) {
  if (!item) return null;
  if (kind === 'event') return { kind, id: item.id, title: item.title || '', when: whenText(item.startsAt, item.endsAt) };
  if (kind === 'banner') {
    const l = item.layers || {};
    return { kind, id: item.id, title: l.headline || l.eyebrow || item.title || '', when: item.state === 'scheduled' ? 'Planlagt' : 'Vises nå', picture: Boolean(item.picture) };
  }
  if (kind === 'notice') return { kind, id: item.id, title: item.title || '', when: '' };
  return null;
}

/** The app's own scheme: opens WhatsApp on the phone with the text ready, the person picks the chat. */
function appLink(text) {
  return 'whatsapp://send?text=' + encodeURIComponent(String(text || '').slice(0, MAX.text));
}
/** The web fallback (WhatsApp Web, or the app through its universal link): social-share's whatsappLink. */
const webLink = (text) => S.whatsappLink(text);

/**
 * What «Åpne» does for a target. A group or a channel: its invite link, which opens it (or offers to join) and carries
 * no text. A contact: wa.me/<number>?text=…, which opens that one chat WITH the text ready — the one case a link can
 * pre-fill.
 */
function openLink(target, text = '') {
  if (!target || !target.inviteUrl) return null;
  if (target.kind === 'contact' && text) return `${target.inviteUrl}?text=${encodeURIComponent(String(text).slice(0, MAX.text))}`;
  return target.inviteUrl;
}

/**
 * Can a message be put INTO a group by a link? No. WhatsApp's invite link opens the group (or joins it); its send
 * link (wa.me/?text=) pre-fills the text but the person chooses the chat; neither does both, and the Business API
 * reaches no group at all. The flow is therefore: share (or copy) the text, open the group, send — and the screens say so.
 */
function canPrefillGroup() {
  return { ok: false, reason_no: 'WhatsApp lar ingen app legge en melding rett inn i en gruppe eller kanal. Del teksten (og bildet) i WhatsApp og velg gruppen der, eller kopier teksten og åpne gruppen.',
    flow: ['share_or_copy', 'open_target', 'paste_and_send'] };
}

/**
 * A share as the phone or the panel reports it, after WhatsApp was opened:
 *   { targetId?, sourceKind, sourceId?, text, withPicture?, device } → { ok, value } or { ok: false, errors }
 * `targets`: the mosque's live targets, so an archived or foreign id is refused. The text is kept (what went out),
 * never a member's name — a source is published content or the staff member's own words.
 */
function shareInput(body, { targets = [] } = {}) {
  const b = body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  const errors = {};
  const allowed = ['targetId', 'sourceKind', 'sourceId', 'text', 'withPicture', 'device'];
  for (const k of Object.keys(b)) if (!allowed.includes(k)) errors[k] = 'Ukjent felt.';
  const sourceKind = SOURCES.includes(b.sourceKind) ? b.sourceKind : null;
  if (!sourceKind) errors.sourceKind = 'Hva ble delt?';
  const sourceId = b.sourceId == null || b.sourceId === '' ? null : String(b.sourceId);
  if (sourceKind && sourceKind !== 'free' && !sourceId) errors.sourceId = 'Hvilken kunngjøring, hvilket arrangement eller banner?';
  else if (sourceKind && sourceKind !== 'free' && !isId(sourceId)) errors.sourceId = 'Ukjent kilde.';
  if (sourceKind === 'free' && sourceId) errors.sourceId = 'Fritekst har ingen kilde.';
  const text = String(b.text == null ? '' : b.text).replace(/\r\n?/g, '\n').trim();
  if (!text) errors.text = 'Teksten mangler.';
  else if (text.length > MAX.text) errors.text = `Teksten kan være høyst ${MAX.text} tegn.`;
  let targetId = null;
  if (b.targetId != null && b.targetId !== '') {
    const t = targets.find((x) => x.id === String(b.targetId) && !x.archivedAt);
    if (!t) errors.targetId = 'Fant ikke gruppen eller kanalen.';
    else targetId = t.id;
  }
  const device = DEVICES.includes(b.device) ? b.device : null;
  if (!device) errors.device = 'Hvor ble det delt fra?';
  if (b.withPicture !== undefined && typeof b.withPicture !== 'boolean') errors.withPicture = 'Ja eller nei.';
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: { targetId, sourceKind, sourceId: sourceKind === 'free' ? null : sourceId, text, withPicture: Boolean(b.withPicture), device } };
}

/**
 * What the activity log keeps (packages/core/audit.js whatsappAudit, prefix whatsapp_). A target: its kind, its NAME
 * (the mosque's own name for its group) and whether it is archived — never the link. A share: the kind of source, the
 * target's name, whether a picture went with it and from where — never the text.
 */
function targetAudit(t) {
  return { whatsapp_kind: t.kind, whatsapp_target: t.name, whatsapp_archived: Boolean(t.archivedAt) };
}
function shareAudit(share, targetName) {
  return { whatsapp_source: share.sourceKind, whatsapp_target: targetName || null, whatsapp_picture: Boolean(share.withPicture), whatsapp_device: share.device };
}

module.exports = {
  KINDS, KIND_NO, SOURCES, SOURCE_NO, DEVICES, MAX, MAX_TARGETS, LOG_LIMIT, FIELDS,
  isId, inviteUrl, targetInput, archive, restore, sortTargets, whenText, shareable, compose, sourceLine,
  appLink, webLink, openLink, canPrefillGroup, shareInput, targetAudit, shareAudit,
};
